import { isMobileDevice } from '@/app/_lib/utils/isMobileDevice';
import { trackOptimizelyEvent } from '@/main/lib/providers/OptimizelyProvider';
import logNativeShareError from '@/main/lib/utils/log/logNativeShareError';

interface ShareData {
  text?: string;
  title?: string;
  url?: string;
}

interface ShareResult {
  success: boolean;
  error?: string;
}

interface UtmParams {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
}

/**
 * Checks if native sharing is available
 * Requires both navigator.share support and mobile device
 */
export const canUseNativeShare = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    'navigator' in window &&
    'share' in window.navigator &&
    typeof window.navigator.share === 'function' &&
    isMobileDevice()
  );
};

/**
 * Triggers native share with error handling
 * @param url - The URL to share
 * @param title - The title for the shared content
 * @param text - The text description for the shared content
 * @returns Promise resolving to ShareResult indicating success/failure
 */
export const shareViaWebApi = async (url: string, title: string, text?: string): Promise<ShareResult> => {
  // Check if native sharing is available
  if (!canUseNativeShare()) {
    const error = new Error('Native share not supported or not on mobile device');
    logNativeShareError(error, url);
    return {
      error: error.message,
      success: false,
    };
  }

  // Validate required parameters
  if (!url) {
    const error = new Error('URL is required for sharing');
    logNativeShareError(error);
    return {
      error: error.message,
      success: false,
    };
  }

  if (!title) {
    const error = new Error('Title is required for sharing');
    logNativeShareError(error);
    return {
      error: error.message,
      success: false,
    };
  }

  const shareData: ShareData = {
    title,
    url,
  };

  if (text) {
    shareData.text = text;
  }

  trackOptimizelyEvent('share_sheet_opens', { shareUrl: url });

  try {
    await window.navigator.share(shareData);
    trackOptimizelyEvent('shares', {
      shareUrl: url,
    });
    return { success: true };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      logNativeShareError(new Error('Share cancelled by user'), url);
      trackOptimizelyEvent('share_sheet_cancels', {
        shareUrl: url,
      });
      return {
        error: 'Share was cancelled by user',
        success: false,
      };
    }

    // Handle other errors - also track as cancellation since share didn't complete
    trackOptimizelyEvent('share_sheet_cancels', {
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      shareUrl: url,
    });

    logNativeShareError(error instanceof Error ? error : new Error('An unknown error occurred during sharing'), url);
    return {
      error: error instanceof Error ? error.message : 'An unknown error occurred during sharing',
      success: false,
    };
  }
};

/**
 * Utility function to share the current page
 * @param customTitle - Optional custom title, defaults to document.title
 * @param customText - Optional custom text description
 * @returns Promise resolving to ShareResult
 */
export const shareCurrentPage = async (
  customTitle?: string,
  customText?: string,
  utmParams: UtmParams = { utmCampaign: 'share_test', utmMedium: 'mobile', utmSource: 'native_share' },
): Promise<ShareResult> => {
  if (typeof window === 'undefined') {
    const error = new Error('Cannot share on server-side');
    logNativeShareError(error);
    return {
      error: error.message,
      success: false,
    };
  }
  const urlObject = new URL(window.location.href);
  urlObject.searchParams.set('utm_source', utmParams.utmSource);
  urlObject.searchParams.set('utm_medium', utmParams.utmMedium);
  urlObject.searchParams.set('utm_campaign', utmParams.utmCampaign);
  const url = urlObject.toString();

  const title = customTitle || document.title;

  return await shareViaWebApi(url, title, customText);
};
